import {
  NO_EMPLOYEE_RECORD_MESSAGE_AR,
  SIGN_IN_REFUSAL_MESSAGES_AR,
  refuseSignInReason,
} from '../../../core/policy/identity';
import type { RegistrationReason } from '../../../core/identity/accountLink';
import type { Role } from '../../../core/policy/roles';
import type { AuthenticatedUser } from './ports/authPort';
import type { IdentityLookup } from './ports/accountLinkPort';

/**
 * The session state machine.
 *
 * Pure: given a user and a lookup result, it says which screen the person
 * belongs on. Keeping the decision here rather than scattering `if` statements
 * through components means the answer to "can this person be here?" has exactly
 * one implementation, and it can be tested without a browser (§5.4).
 *
 * [ABSOLUTE] This decides *navigation*, not authorization. The security rules
 * decide what data anyone may touch. A bug here shows the wrong screen; it
 * cannot expose another employee's record.
 */

export type SessionState =
  /** Still resolving — no decision may be made yet. */
  | { readonly kind: 'loading' }
  /** Nobody signed in. */
  | { readonly kind: 'signed_out' }
  /** Signed in, but not admissible. Carries the reason to display. */
  | { readonly kind: 'refused'; readonly messageAr: string }
  /** A record matches this address; the person must confirm it is theirs. */
  | {
      readonly kind: 'needs_confirmation';
      readonly employeeName: string;
      readonly candidateToken: string;
    }
  /**
   * No record matches, or they declined one. They self-report.
   *
   * `reason` is carried explicitly rather than inferred from `messageAr`.
   * Deriving intent from display copy breaks the moment someone rewords it,
   * and here it would decide whether HR is told to fix an import row or to add
   * a missing person — a wrong answer that looks like nothing is wrong.
   */
  | {
      readonly kind: 'needs_registration';
      readonly reason: RegistrationReason;
      readonly messageAr: string;
    }
  /** Reported, waiting for HR. No data entry until approved (FR-008). */
  | { readonly kind: 'pending_approval' }
  /** Linked. The application proper is reachable. */
  | { readonly kind: 'ready'; readonly roles: readonly Role[] };

/**
 * Decides the state from a verified user and the server's lookup.
 *
 * `lookup` is null while it is still in flight — distinct from a lookup that
 * returned "no match". Collapsing those two is how a loading screen turns into
 * a wrongly-shown registration form.
 */
export function deriveSessionState(
  user: AuthenticatedUser | null,
  lookup: IdentityLookup | null,
): SessionState {
  if (user === null) return { kind: 'signed_out' };

  // Domain and verification are checked before anything else. This mirrors the
  // security rules rather than replacing them.
  const refusal = refuseSignInReason({
    email: user.email,
    emailVerified: user.emailVerified,
  });
  if (refusal) {
    return { kind: 'refused', messageAr: SIGN_IN_REFUSAL_MESSAGES_AR[refusal] };
  }

  if (lookup === null) return { kind: 'loading' };

  switch (lookup.outcome) {
    case 'already_linked':
      return { kind: 'ready', roles: user.roles };

    case 'match':
      return {
        kind: 'needs_confirmation',
        employeeName: lookup.employeeName,
        candidateToken: lookup.candidateToken,
      };

    case 'awaiting_approval':
      return { kind: 'pending_approval' };

    case 'no_match':
      /*
       * The server reports `no_match` both when no row carries this address
       * and when the person previously declined the row that did. It is
       * `no_matching_record` here because a decline was already recorded, with
       * its own audit entry and its own flag on the mismatched row — filing a
       * second `name_mismatch` would double-report the same fact.
       */
      return {
        kind: 'needs_registration',
        reason: 'no_matching_record',
        messageAr: NO_EMPLOYEE_RECORD_MESSAGE_AR,
      };
  }
}

/** Whether the application's own screens are reachable in this state. */
export function canEnterApplication(state: SessionState): boolean {
  return state.kind === 'ready';
}

/**
 * Whether the person may enter any data at all.
 *
 * Separate from `canEnterApplication` because the project owner chose that a
 * pending applicant sees a waiting screen and enters nothing — not a partially
 * usable form (FR-008).
 */
export function canEnterData(state: SessionState): boolean {
  return state.kind === 'ready';
}
